import React, { useState, useMemo } from "react";
import { GoogleMap, MarkerF, InfoWindowF, useJsApiLoader } from "@react-google-maps/api";
import { useTheme } from "@mui/material"; // ✅ Import Theme Hook
import { formatPrice, currencyOptions } from "../utils/formatPrice";

// 🌑 DARK MODE MAP STYLE (Midnight Theme)
const darkMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#263c3f" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#6b9a76" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#38414e" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#212a37" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9ca5b3" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#746855" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2835" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#f3d19c" }],
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#2f3948" }],
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#17263c" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#515c6d" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#17263c" }],
  },
];

const MapView = ({ listings, onMarkerClick, currency, selectedListing: propSelectedListing }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [activeTier, setActiveTier] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);

  // Sync with prop
  React.useEffect(() => {
    if (propSelectedListing) {
      setSelectedListing(propSelectedListing);
      setImageIndex(0);
    }
  }, [propSelectedListing]);

  // 🎨 SOLID COLORS
  const TIER_STYLES = {
    economy: { color: "#2E7D32", label: "Economy" },
    standard: { color: "#F57C00", label: "Standard" },
    premium: { color: "#D32F2F", label: "Premium" },
    default: { color: "#333333", label: "Other" },
  };

  const getCoords = (listing) => {
    const lat =
      listing.lat ||
      listing.latitude ||
      listing.coordinate?.latitude ||
      listing.coordinate?.lat;
    const lng =
      listing.lng ||
      listing.longitude ||
      listing.coordinate?.longitude ||
      listing.coordinate?.lng;
    return { lat: parseFloat(lat), lng: parseFloat(lng) };
  };

  const allValidListings = listings.filter((l) => {
    const { lat, lng } = getCoords(l);
    return !isNaN(lat) && !isNaN(lng);
  });

  const tierCounts = allValidListings.reduce((acc, l) => {
    const t = l.tier || "default";
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  const validListings = allValidListings.filter(
    (l) => !activeTier || l.tier === activeTier
  );

  // Memoize center so map doesn't re-pan on every selectedListing change, unless prop changes
  const center = useMemo(() => {
    if (selectedListing) {
      return getCoords(selectedListing);
    }
    if (!validListings.length) return { lat: 48.8566, lng: 2.3522 };
    return {
      lat: validListings.reduce((sum, l) => sum + getCoords(l).lat, 0) / validListings.length,
      lng: validListings.reduce((sum, l) => sum + getCoords(l).lng, 0) / validListings.length,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTier, listings, selectedListing]);

  const selectListing = (listing) => {
    setSelectedListing(listing);
    setImageIndex(0);
  };

  if (!isLoaded) return <div>Loading Map...</div>;

  const mapHeight = window.innerWidth < 600 ? "60vh" : "80vh";

  return (
    <div style={{ position: "relative", width: "100%", height: mapHeight }}>
      <GoogleMap
        mapContainerStyle={{
          width: "100%",
          height: "100%",
        }}
        center={center}
        zoom={13}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
          gestureHandling: "greedy", // scroll to zoom without holding Ctrl
          styles: isDarkMode ? darkMapStyle : [],
        }}
        onClick={() => { setSelectedListing(null); setImageIndex(0); }}
      >
        {validListings.map((listing, index) => {
          const tier = listing.tier || "default";
          const style = TIER_STYLES[tier] || TIER_STYLES.default;

          return (
            <MarkerF
              key={listing.id}
              position={getCoords(listing)}
              zIndex={1000 + index}
              label={{
                text: `${currencyOptions.find(c => c.code === (currency || "USD"))?.symbol || "$"}${Math.round(listing.price?.total || 0)}`,
                color: "white",
                fontWeight: "bold",
                fontSize: "13px",
                fontFamily: "sans-serif",
              }}
              icon={{
                path: "M -24, -13 H 24 C 29, -13 29, 13 24, 13 H -24 C -29, 13 -29, -13 -24, -13 Z",
                fillColor: style.color,
                fillOpacity: 1,
                strokeColor: isDarkMode ? "#ffffff" : "white", // Border stays white for contrast
                strokeWeight: 2.5,
                scale: 1,
                labelOrigin: { x: 0, y: 1 },
              }}
              onClick={() => selectListing(listing)}
            />
          );
        })}

        {selectedListing && (
          <InfoWindowF
            position={getCoords(selectedListing)}
            onCloseClick={() => { setSelectedListing(null); setImageIndex(0); }}
            options={{ pixelOffset: new window.google.maps.Size(0, -40) }}
          >
            <div style={{
              width: 270,
              fontFamily: "'Roboto', 'Helvetica', sans-serif",
              borderRadius: 12,
              overflow: "hidden",
              backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
            }}>
              <style>{`
                .gm-ui-hover-effect { display: none !important; }
                .gm-style-iw-c { padding: 0 !important; border-radius: 12px !important; box-shadow: 0 6px 24px rgba(0,0,0,0.3) !important; background-color: ${isDarkMode ? "#1e1e1e" : "#ffffff"} !important; }
                .gm-style-iw-d { overflow: hidden !important; border-radius: 12px !important; }
                .gm-style-iw-t::after { background: ${isDarkMode ? "#1e1e1e" : "#ffffff"} !important; }
              `}</style>

              {/* Image with arrows */}
              {selectedListing.images?.length > 0 && (() => {
                const imgs = selectedListing.images.map(i => i.replace(/^"|"$/g, ""));
                const total = imgs.length;
                return (
                  <div style={{ position: "relative" }}>
                    <a href={selectedListing.deeplink} target="_blank" rel="noopener noreferrer" style={{ display: "block" }}>
                      <img
                        src={imgs[imageIndex]}
                        alt={selectedListing.name}
                        style={{ width: "100%", height: 150, objectFit: "cover", display: "block" }}
                      />
                    </a>
                    {total > 1 && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); setImageIndex((imageIndex - 1 + total) % total); }}
                          style={{
                            position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)",
                            background: "rgba(0,0,0,0.45)", border: "none", borderRadius: "50%",
                            width: 28, height: 28, cursor: "pointer", color: "white",
                            fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                        >‹</button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setImageIndex((imageIndex + 1) % total); }}
                          style={{
                            position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                            background: "rgba(0,0,0,0.45)", border: "none", borderRadius: "50%",
                            width: 28, height: 28, cursor: "pointer", color: "white",
                            fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                        >›</button>
                        <div style={{
                          position: "absolute", bottom: 6, right: 8,
                          background: "rgba(0,0,0,0.45)", borderRadius: 10,
                          padding: "2px 7px", fontSize: 11, color: "white",
                        }}>
                          {imageIndex + 1}/{total}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              <div style={{ padding: "12px 14px 14px" }}>
                {/* Title — links to Airbnb */}
                <a href={selectedListing.deeplink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  <div style={{
                    fontWeight: "bold", fontSize: 14,
                    color: isDarkMode ? "#eeeeee" : "#111111",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    marginBottom: 6,
                    lineHeight: 1.4,
                  }}>
                    {selectedListing.name}
                  </div>
                </a>

                {/* Price + Rating row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: isDarkMode ? "#dddddd" : "#222222" }}>
                    {formatPrice(Math.round(selectedListing.price?.total || selectedListing.price?.rate || 0), currency || "USD", false)}
                    <span style={{ fontWeight: 400, fontSize: 12, color: isDarkMode ? "#aaaaaa" : "#666666", marginLeft: 3 }}>total</span>
                  </span>
                  {selectedListing.rating && (
                    <span style={{ fontSize: 13, color: isDarkMode ? "#aaaaaa" : "#555555" }}>
                      ⭐ {selectedListing.rating}
                      {selectedListing.reviewsCount ? ` (${selectedListing.reviewsCount})` : ""}
                    </span>
                  )}
                </div>

                {/* Analyze button */}
                <button
                  onClick={(e) => { e.stopPropagation(); onMarkerClick && onMarkerClick(selectedListing); setSelectedListing(null); }}
                  style={{
                    width: "100%", padding: "10px 0", border: "none", borderRadius: 8,
                    backgroundColor: "#5F5AA2",
                    color: "white", fontWeight: 700, fontSize: 14, cursor: "pointer",
                    letterSpacing: "0.02em",
                  }}
                >
                  Analyze Listing
                </button>
              </div>
            </div>
          </InfoWindowF>
        )}
      </GoogleMap>

      {/* 📝 LEGEND / FILTER */}
      <div
        style={{
          position: "absolute",
          top: "10px",
          left: "10px",
          backgroundColor: isDarkMode ? "rgba(30, 30, 30, 0.95)" : "white",
          color: isDarkMode ? "white" : "#222",
          padding: "12px",
          borderRadius: "8px",
          boxShadow: isDarkMode ? "0 4px 12px rgba(0,0,0,0.5)" : "0 2px 8px rgba(0,0,0,0.2)",
          fontSize: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          zIndex: 50,
          border: isDarkMode ? "1px solid #444" : "none",
        }}
      >
        <div style={{ fontWeight: "bold", fontSize: "13px", marginBottom: "2px" }}>
          Filter by Tier
        </div>

        {/* All option */}
        <div
          onClick={() => setActiveTier(null)}
          style={{
            display: "flex", alignItems: "center", gap: "8px", cursor: "pointer",
            padding: "3px 6px", borderRadius: "5px",
            backgroundColor: activeTier === null
              ? (isDarkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)")
              : "transparent",
            fontWeight: activeTier === null ? "bold" : "normal",
          }}
        >
          <div style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#888", border: "2px solid white" }} />
          <span>All</span>
          <span style={{ marginLeft: "auto", opacity: 0.6, fontSize: "11px" }}>{allValidListings.length}</span>
        </div>

        {Object.entries(TIER_STYLES).map(([key, style]) =>
          key !== "default" && (
            <div
              key={key}
              onClick={() => setActiveTier(activeTier === key ? null : key)}
              style={{
                display: "flex", alignItems: "center", gap: "8px", cursor: "pointer",
                padding: "3px 6px", borderRadius: "5px",
                backgroundColor: activeTier === key
                  ? (isDarkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)")
                  : "transparent",
                fontWeight: activeTier === key ? "bold" : "normal",
                opacity: activeTier && activeTier !== key ? 0.45 : 1,
              }}
            >
              <div style={{
                width: "12px", height: "12px", borderRadius: "50%",
                backgroundColor: style.color, border: "2px solid white",
                boxShadow: "0 0 2px rgba(0,0,0,0.3)",
              }} />
              <span style={{ textTransform: "capitalize" }}>{style.label}</span>
              <span style={{ marginLeft: "auto", opacity: 0.6, fontSize: "11px" }}>{tierCounts[key] || 0}</span>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default MapView;
